<template>
  <div class="markdown-editor-wrapper">
    <editor
      v-if="editorMounted"
      ref="toastuiEditor"
      :initial-value="value"
      :initial-edit-type="initialEditType"
      :height="height"
      :options="editorOptions"
      :preview-style="previewStyle"
      @change="onEditorChange"
    />
  </div>
</template>

<script>
import '@toast-ui/editor/dist/toastui-editor.css';
import { Editor } from '@toast-ui/vue-editor';

export default {
  name: 'AppMarkdownEditor',
  components: {
    Editor
  },
  props: {
    value: {
      type: String,
      default: ''
    },
    editable: {
      type: Boolean,
      default: true
    },
    minHeight: {
      type: String,
      default: '400px'
    },
    initialEditType: {
      type: String,
      default: 'wysiwyg', // 'wysiwyg' or 'markdown'
      validator: (value) => ['wysiwyg', 'markdown'].includes(value)
    },
    previewStyle: {
      type: String,
      default: 'vertical', // 'tab' or 'vertical'
      validator: (value) => ['tab', 'vertical'].includes(value)
    }
  },
  data() {
    return {
      editorMounted: false,
      isUpdatingFromProp: false,
      editorOptions: {
        hideModeSwitch: false,
        toolbarItems: [
          ['heading', 'bold', 'italic', 'strike'],
          ['hr', 'quote'],
          ['ul', 'ol', 'task'],
          ['table', 'link'],
          ['code', 'codeblock']
        ],
        language: this.getEditorLanguage(),
        useCommandShortcut: true
      }
    };
  },
  computed: {
    height() {
      return this.minHeight;
    }
  },
  watch: {
    value(newValue) {
      if (this.isUpdatingFromProp) return;

      const editor = this.$refs.toastuiEditor;
      if (editor) {
        const editorInstance = editor.invoke('getEditorType') === 'markdown'
          ? editor.invoke('getMarkdown')
          : editor.invoke('getMarkdown');

        if (editorInstance !== newValue) {
          this.isUpdatingFromProp = true;
          editor.invoke('setMarkdown', newValue || '');
          this.$nextTick(() => {
            this.isUpdatingFromProp = false;
          });
        }
      }
    },
    editable(newVal) {
      // Toast UI Editor doesn't have a direct editable prop
      // We can disable toolbar buttons if needed
      if (!newVal && this.$refs.toastuiEditor) {
        // Make editor readonly by disabling editing
        const editorEl = this.$el.querySelector('.toastui-editor-contents');
        if (editorEl) {
          editorEl.setAttribute('contenteditable', 'false');
        }
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.editorMounted = true;
    });
  },
  beforeDestroy() {
    if (this.$refs.toastuiEditor) {
      const editor = this.$refs.toastuiEditor;
      if (editor.invoke) {
        editor.invoke('destroy');
      }
    }
  },
  methods: {
    getEditorLanguage() {
      // Map Vue i18n locale to Toast UI Editor language
      const locale = this.$i18n ? this.$i18n.locale : 'fr';
      const languageMap = {
        'fr': 'fr-FR',
        'en': 'en-US',
        'de': 'de-DE'
      };
      return languageMap[locale] || 'fr-FR';
    },

    onEditorChange() {
      if (this.isUpdatingFromProp) return;

      const editor = this.$refs.toastuiEditor;
      if (editor) {
        const markdown = editor.invoke('getMarkdown');
        this.$emit('input', markdown);
      }
    },

    getMarkdown() {
      const editor = this.$refs.toastuiEditor;
      return editor ? editor.invoke('getMarkdown') : '';
    },

    setMarkdown(markdown) {
      const editor = this.$refs.toastuiEditor;
      if (editor) {
        editor.invoke('setMarkdown', markdown || '');
      }
    },

    getHTML() {
      const editor = this.$refs.toastuiEditor;
      return editor ? editor.invoke('getHTML') : '';
    },

    reset() {
      this.setMarkdown('');
    }
  }
};
</script>

<style scoped>
.markdown-editor-wrapper {
  border: 1px solid #dcdee2;
  border-radius: 4px;
  overflow: hidden;
}

/* Match iView focus styles */
.markdown-editor-wrapper:focus-within {
  border-color: #57a3f3;
  box-shadow: 0 0 0 2px rgba(45, 140, 240, 0.2);
}

/* Error state for form validation */
.ivu-form-item-error .markdown-editor-wrapper {
  border-color: #ed4014;
}

.ivu-form-item-error .markdown-editor-wrapper:focus-within {
  border-color: #ed4014;
  box-shadow: 0 0 0 2px rgba(237, 64, 20, 0.2);
}

/* Override Toast UI Editor styles to match iView theme */
.markdown-editor-wrapper >>> .toastui-editor-defaultUI {
  border: none;
}

.markdown-editor-wrapper >>> .toastui-editor-toolbar {
  background-color: #f8f8f9;
  border-bottom: 1px solid #dcdee2;
}

.markdown-editor-wrapper >>> .toastui-editor-mode-switch {
  border-right: 1px solid #dcdee2;
}

.markdown-editor-wrapper >>> .toastui-editor-contents {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

/* Make sure editor respects min-height */
.markdown-editor-wrapper >>> .toastui-editor-defaultUI-toolbar {
  flex-wrap: wrap;
}
</style>
